import { z } from "zod";

import type { ILegalRepository } from "@/core/repositories/interfaces/ILegalRepository";
import type { ILegalService } from "@/core/services/interfaces/ILegalService";
import type {
  LegalDocument,
  UpsertLegalDocumentInput,
} from "@/core/services/types/legal.types";
import { LEGAL_DOCUMENT_SLUGS } from "@/core/services/types/legal.types";

const upsertSchema = z.object({
  document_id: z.string().uuid().optional(),
  slug: z.enum(LEGAL_DOCUMENT_SLUGS),
  title: z.string().trim().min(1),
  content_md: z.string(),
  version: z.string().trim().min(1).optional(),
  locale: z.string().trim().min(1).optional(),
  is_published: z.boolean().optional(),
  effective_at: z.string().optional(),
});

const donateLinkSchema = z
  .string()
  .trim()
  .refine(
    (value) => value === "" || z.url().safeParse(value).success,
    { message: "Invalid URL" },
  );

export class LegalService implements ILegalService {
  constructor(private readonly repository: ILegalRepository) {}

  async listAll(): Promise<LegalDocument[]> {
    return this.repository.findAll();
  }

  async upsert(input: UpsertLegalDocumentInput): Promise<LegalDocument | null> {
    const parsed = upsertSchema.safeParse(input);
    if (!parsed.success) {
      console.error("[LegalService.upsert] validation", parsed.error.flatten());
      return null;
    }

    return this.repository.upsert(parsed.data);
  }

  async getDonateLink(): Promise<string> {
    return this.repository.getDonateLink();
  }

  async updateDonateLink(url: string): Promise<string | null> {
    const parsed = donateLinkSchema.safeParse(url);
    if (!parsed.success) {
      console.error(
        "[LegalService.updateDonateLink] validation",
        parsed.error.flatten(),
      );
      return null;
    }

    return this.repository.updateDonateLink(parsed.data);
  }
}
